import { Table } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";
import { BarChartOutlined, CalendarOutlined } from "@ant-design/icons";

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "BILLDATE", label: "Bill Date" },
  { key: "GSTAMT", label: "GST Amt", badge: "pieces" },
  { key: "AMOUNT", label: "Amount", badge: "netwt" },
];
const secondaryFields = [
  { key: "CGSTAMT", label: "CGST Amt" },
  { key: "SGSTAMT", label: "SGST Amt" },
  { key: "IGSTAMT", label: "IGST Amt" },
];

const DateWiseSale = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ── fetch date wise sale for the selected date range ── */
  useEffect(() => {
    if (dates[0] && dates[1]) {
      const fromDate = dates[0].format("MM/DD/YYYY");
      const toDate = dates[1].format("MM/DD/YYYY");

      setLoading(true);
      setTableVisible(false);

      axios
        .get(
          `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=BILL_ITEMS_DET&where=BILLDATE>='${fromDate}'%20AND%20BILLDATE<='${toDate}'&order=BILLNO%2CSNO`,
          {
            headers: {
              tenantName: tenantName,
            },
          },
        )
        .then((response) => {
          const detailsData = Array.isArray(response.data) ? response.data : [];

          const dateMap = {};
          detailsData.forEach((item) => {
            const rawDate = item?.billdate || item?.BILLDATE;
            if (!rawDate) return;
            const dateKey = dayjs(rawDate).format("YYYY-MM-DD");

            const itemType = String(item?.TYPE || "").trim().toUpperCase();
            const salesType = String(item?.SALESTYPE || "").trim().toUpperCase();
            const isDeduction =
              itemType === "EB" ||
              itemType === "RB" ||
              salesType.includes("RETURN") ||
              salesType.includes("EXCHANGE");

            const rawAmt = Number(item?.amount ?? item?.AMOUNT ?? 0);
            const rawGst = Number(item?.GSTAMT ?? item?.gstamt ?? 0);
            const rawCgst = Number(item?.CGSTAMT ?? item?.cgstamt ?? 0);
            const rawIgst = Number(item?.IGSTAMT ?? item?.igstamt ?? 0);
            const rawSgst = Number(item?.SGSTAMT ?? item?.sgstamt ?? 0);

            const amt = isDeduction ? -Math.abs(rawAmt) : rawAmt;
            const gstAmt = isDeduction ? -Math.abs(rawGst) : rawGst;
            const cgstAmt = isDeduction ? -Math.abs(rawCgst) : rawCgst;
            const igstAmt = isDeduction ? -Math.abs(rawIgst) : rawIgst;
            const sgstAmt = isDeduction ? -Math.abs(rawSgst) : rawSgst;

            if (!dateMap[dateKey]) {
              dateMap[dateKey] = {
                BILLDATE: rawDate,
                dateKey,
                AMOUNT: 0,
                GSTAMT: 0,
                CGSTAMT: 0,
                IGSTAMT: 0,
                SGSTAMT: 0,
              };
            }
            dateMap[dateKey].AMOUNT += amt;
            dateMap[dateKey].GSTAMT += gstAmt;
            dateMap[dateKey].CGSTAMT += cgstAmt;
            dateMap[dateKey].IGSTAMT += igstAmt;
            dateMap[dateKey].SGSTAMT += sgstAmt;
          });

          // Sort by date ascending
          const sortedEntries = Object.values(dateMap).sort((a, b) =>
            a.dateKey.localeCompare(b.dateKey),
          );

          const calculatedData = sortedEntries.map((row, index) => ({
            ...row,
            key: index + 1,
            serialNo: index + 1,
          }));

          setFilteredData(calculatedData);
          setTimeout(() => setTableVisible(true), 80);
        })
        .catch((error) => {
          console.error("Error fetching date wise sale:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [dates]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Bill Date",
      dataIndex: "BILLDATE",
      key: "BILLDATE",
      align: "center",
      className: "col-date",
    },
    {
      title: "GST AMT",
      dataIndex: "GSTAMT",
      key: "GSTAMT",
      align: "right",
      className: "col-tagno",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "CGST AMT",
      dataIndex: "CGSTAMT",
      key: "CGSTAMT",
      align: "right",
    },
    {
      title: "IGST AMT",
      dataIndex: "IGSTAMT",
      key: "IGSTAMT",
      align: "right",
    },
    {
      title: "SGST AMT",
      dataIndex: "SGSTAMT",
      key: "SGSTAMT",
      align: "right",
    },
    {
      title: "Amount",
      dataIndex: "AMOUNT",
      key: "AMOUNT",
      align: "right",
      className: "col-netwt",
      render: (value) => <span className="wtBadge">{value}</span>,
    },
  ];

  /* ── totals (blank values count as 0) ── */
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.amount += Number(item?.AMOUNT || 0);
        acc.gst += Number(item?.GSTAMT || 0);
        acc.cgst += Number(item?.CGSTAMT || 0);
        acc.igst += Number(item?.IGSTAMT || 0);
        acc.sgst += Number(item?.SGSTAMT || 0);
        return acc;
      },
      { amount: 0, gst: 0, cgst: 0, igst: 0, sgst: 0 },
    );
  }, [filteredData]);

  const totalAmount = totals.amount.toFixed(2);
  const totalGST = totals.gst.toFixed(2);
  const totalCGST = totals.cgst.toFixed(2);
  const totalIGST = totals.igst.toFixed(2);
  const totalSGST = totals.sgst.toFixed(2);

  /* rows shown in the table (dates pre-formatted, no total row) */
  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        AMOUNT: Number(item?.AMOUNT || 0).toFixed(2),
        GSTAMT: Number(item?.GSTAMT || 0).toFixed(2),
        CGSTAMT: Number(item?.CGSTAMT || 0).toFixed(2),
        IGSTAMT: Number(item?.IGSTAMT || 0).toFixed(2),
        SGSTAMT: Number(item?.SGSTAMT || 0).toFixed(2),
        BILLDATE: item?.BILLDATE
          ? dayjs(item.BILLDATE).format("DD/MMM/YYYY")
          : "",
      })),
    [filteredData],
  );

  /* export data = rows + total row */
  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      BILLDATE: "",
      GSTAMT: totalGST,
      CGSTAMT: totalCGST,
      IGSTAMT: totalIGST,
      SGSTAMT: totalSGST,
      AMOUNT: totalAmount,
    },
  ];

  return (
    <div className={styles.page}>
      <InventoryPageHeader
        title="Date Wise Sale"
        subtitle="Point of Sale · Date Wise Sale"
        icon={<BarChartOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          {
            label: "Records",
            value: filteredData.length,
            color: "blue",
            decimals: 0,
          },
          {
            label: "GST Amt",
            value: totals.gst,
            color: "orange",
            decimals: 2,
          },
          {
            label: "Amount",
            value: totals.amount,
            color: "green",
            decimals: 2,
          },
        ]}
      />

      <div className={styles.toolbar} style={{ flexWrap: "wrap", rowGap: 12 }}>
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", rowGap: 12 }}
        >
          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Start Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[0]}
                onChange={(d) => setDates([d || dayjs(), dates[1]])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>

          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>End Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[1]}
                onChange={(d) => setDates([dates[0], d || dayjs()])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="DateWiseSale"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="key"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No date wise sale records found for the selected dates"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">{totalGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalCGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalIGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalSGST}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">{totalAmount}</Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <style>{`
  @keyframes mainProductPulse {
    0%   { box-shadow: 0 0 0 0 rgba(24,144,255,0.30); }
    70%  { box-shadow: 0 0 0 8px rgba(24,144,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(24,144,255,0); }
  }
  .mainProductHighlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 10px;
    border-radius: 14px;
    background: #eef6ff;
    border: 1.5px solid #65aefd;
    box-shadow: 0 0 0 1.5px #bcdcff inset;
    animation: mainProductPulse 2.4s infinite;
    min-width: 210px;
    flex-shrink: 0;
  }
  .mainProductIcon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(24,144,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1677ff;
    flex-shrink: 0;
  }
  .mainProductInner {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
  }
  .mainProductPicker.ant-picker {
    height: 34px !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d6e8ff !important;
    width: 100%;
  }
  .mainProductPicker.ant-picker:hover {
    border-color: #65aefd !important;
  }
  .mainProductPicker.ant-picker-focused {
    border-color: #1890ff !important;
    box-shadow: none !important;
  }
  .mainProductPicker .ant-picker-input > input {
    font-size: 13px;
    font-weight: 600;
    color: #1f2430;
  }
  .ant-table-thead > tr > th {
    white-space: nowrap !important;
  }
`}</style>
    </div>
  );
};

export default DateWiseSale;    